/*
 * Pure helpers of the "Frühwarnung bei Abwesenheiten" pages (rule form, texts, tones, backend detection).
 * Contract: workflow-manager `absence-early-warning/*`, employee-handler `quality-management/*`.
 */
import {Types} from 'mongoose';
import {isAxiosError} from 'axios';
import {StateCodes} from '@/enums/StateCodes.enum';
import {
    ABSENCE_EARLY_WARNING_DEFAULTS,
    AbsenceEarlyWarningMailSkipReason,
    AbsenceEarlyWarningMailStatus,
    AbsenceEarlyWarningTriggerMode,
} from '@/enums/workflow-manager/AbsenceEarlyWarning.enum';
import {WorkflowType} from '@/enums/workflow-manager/WorkflowType.enum';
import {
    AbsenceEarlyWarningRunSummary,
    AbsenceEarlyWarningWorkflow,
} from '@/documentTypes/workflow-manager/AbsenceEarlyWarningWorkflow.document';
import {AbsenceEarlyWarningWorkflowRequestData} from '@/classes/dto/workflow-manager/CreateWorkflow.request.dto';
import {UpdateWorkflowRequestDto} from '@/classes/dto/workflow-manager/UpdateWorkflow.request.dto';
import {
    AbsenceEarlyWarningConfigTemplateDto,
} from '@/classes/dto/workflow-manager/absence-early-warning/AbsenceEarlyWarningConfig.template.dto';
import {AbsenceEarlyWarningRecentItem} from '@/documentTypes/workflow-manager/AbsenceEarlyWarningRecord.document';
import {
    QualityManagementResolutionSource,
    QualityManagementStateOverviewItemDto,
} from '@/classes/dto/employee-handler/quality-management/response/QualityManagementResolution.response.dto';
import {CoursePresenceState} from '@/enums/CoursePresenceState.enum';

export type Tone = 'good' | 'warn' | 'bad' | 'info' | 'muted' | 'teal';

/** Editable state of a rule (unsaved or saved workflow) */
export type EarlyWarningRuleForm = {
    name: string;
    states: StateCodes[];
    triggerMode: AbsenceEarlyWarningTriggerMode;
    threshold: number;
    window: number;
    countExcused: boolean;
    cooldownWeeks: number;
    toDo: boolean;
    parentMail: boolean;
    /** Empty = default template of the type */
    mailTemplate: string;
    isPaused: boolean;
};

export type EarlyWarningRule = Partial<AbsenceEarlyWarningWorkflow> & { _id: Types.ObjectId; name: string; type: WorkflowType };

/** Statuses of optional endpoints: an old backend answers with 404/405/501 (route unknown) */
export const MISSING_BACKEND_STATUSES = [404, 405, 501];
/** Optional loads that must never open the global error/unauthorized dialogs */
export const SILENT = {handledStatuses: [403, 404, 405, 501]};

export const httpStatus = (error: unknown): number | undefined => isAxiosError(error) ? error.response?.status : undefined;

export const isMissingBackend = (error: unknown): boolean => MISSING_BACKEND_STATUSES.includes(httpStatus(error) ?? 0);

export const isForbidden = (error: unknown): boolean => httpStatus(error) === 403;

export const defaultRuleForm = (): EarlyWarningRuleForm => ({
    name: '',
    states: [],
    triggerMode: ABSENCE_EARLY_WARNING_DEFAULTS.triggerMode,
    threshold: ABSENCE_EARLY_WARNING_DEFAULTS.threshold,
    window: ABSENCE_EARLY_WARNING_DEFAULTS.window,
    countExcused: ABSENCE_EARLY_WARNING_DEFAULTS.countExcused,
    cooldownWeeks: ABSENCE_EARLY_WARNING_DEFAULTS.cooldownWeeks,
    toDo: true,
    parentMail: true,
    mailTemplate: '',
    isPaused: false,
});

export const ruleFormFromWorkflow = (rule: EarlyWarningRule): EarlyWarningRuleForm => {
    const d = defaultRuleForm();
    return {
        name: rule.name || '',
        states: Array.isArray(rule.states) ? [...rule.states] : [],
        triggerMode: rule.triggerMode || d.triggerMode,
        threshold: rule.threshold ?? d.threshold,
        window: rule.window ?? d.window,
        countExcused: rule.countExcused ?? d.countExcused,
        cooldownWeeks: rule.cooldownWeeks ?? d.cooldownWeeks,
        toDo: rule.actions?.toDo ?? true,
        parentMail: rule.actions?.parentMail ?? true,
        mailTemplate: rule.mailTemplate ? String(rule.mailTemplate) : '',
        isPaused: !!rule.isPaused,
    };
};

const toInt = (value: unknown, fallback: number): number => {
    const n = Math.round(Number(value));
    return Number.isFinite(n) ? n : fallback;
};

/** Normalised numbers of the form (inputs deliver strings) */
export const normaliseRuleForm = (form: EarlyWarningRuleForm): EarlyWarningRuleForm => ({
    ...form,
    name: form.name.trim(),
    threshold: toInt(form.threshold, ABSENCE_EARLY_WARNING_DEFAULTS.threshold),
    window: toInt(form.window, ABSENCE_EARLY_WARNING_DEFAULTS.window),
    cooldownWeeks: toInt(form.cooldownWeeks, ABSENCE_EARLY_WARNING_DEFAULTS.cooldownWeeks),
});

/** German error texts of the form; empty if the rule can be saved */
export const validateRuleForm = (raw: EarlyWarningRuleForm, requireName = true): string[] => {
    const form = normaliseRuleForm(raw);
    const errors: string[] = [];
    if (requireName && !form.name) {
        errors.push('Bitte gib der Regel einen Namen.');
    }
    if (form.name.length > 64) {
        errors.push('Der Name darf höchstens 64 Zeichen lang sein.');
    }
    if (!form.states.length) {
        errors.push('Wähle mindestens ein Bundesland.');
    }
    if (form.threshold < 1 || form.threshold > 20) {
        errors.push('Die Anzahl der Stunden muss zwischen 1 und 20 liegen.');
    }
    if (form.triggerMode === AbsenceEarlyWarningTriggerMode.RATIO && (form.window < form.threshold || form.window > 30)) {
        errors.push('Die Anzahl der betrachteten Stunden muss mindestens so groß wie die Fehlstunden und höchstens 30 sein.');
    }
    if (form.cooldownWeeks < 0 || form.cooldownWeeks > 52) {
        errors.push('Die Ruhezeit muss zwischen 0 und 52 Wochen liegen.');
    }
    if (!form.toDo && !form.parentMail) {
        errors.push('Wähle mindestens eine Aktion (To-do oder Mail).');
    }
    return errors;
};

export const toDryRunConfig = (raw: EarlyWarningRuleForm): AbsenceEarlyWarningConfigTemplateDto => {
    const form = normaliseRuleForm(raw);
    return {
        states: form.states,
        triggerMode: form.triggerMode,
        threshold: form.threshold,
        window: form.triggerMode === AbsenceEarlyWarningTriggerMode.RATIO ? form.window : undefined,
        countExcused: form.countExcused,
        cooldownWeeks: form.cooldownWeeks,
    };
};

export const toCreateData = (raw: EarlyWarningRuleForm): AbsenceEarlyWarningWorkflowRequestData => {
    const form = normaliseRuleForm(raw);
    return {
        type: WorkflowType.ABSENCE_EARLY_WARNING,
        ...toDryRunConfig(form),
        actions: {toDo: form.toDo, parentMail: form.parentMail},
        mailTemplate: form.mailTemplate ? form.mailTemplate as unknown as Types.ObjectId : undefined,
        isPaused: form.isPaused,
    };
};

/** Only the changed fields (an empty object means nothing to save) */
export const toUpdateDto = (raw: EarlyWarningRuleForm, original: EarlyWarningRuleForm): UpdateWorkflowRequestDto => {
    const form = normaliseRuleForm(raw);
    const dto: UpdateWorkflowRequestDto = {};
    if (form.name !== original.name) {
        dto.name = form.name;
    }
    if (form.isPaused !== original.isPaused) {
        dto.isPaused = form.isPaused;
    }
    if ([...form.states].sort().join() !== [...original.states].sort().join()) {
        dto.states = form.states;
    }
    const simple = ['triggerMode', 'threshold', 'countExcused', 'cooldownWeeks'] as const;
    for (const key of simple) {
        if (form[key] !== original[key]) {
            (dto as Record<string, unknown>)[key] = form[key];
        }
    }
    if (form.triggerMode === AbsenceEarlyWarningTriggerMode.RATIO && form.window !== original.window) {
        dto.window = form.window;
    }
    if (form.toDo !== original.toDo || form.parentMail !== original.parentMail) {
        dto.actions = {toDo: form.toDo, parentMail: form.parentMail};
    }
    if (form.mailTemplate !== original.mailTemplate) {
        dto.mailTemplate = form.mailTemplate ? form.mailTemplate as unknown as Types.ObjectId : null;
    }
    return dto;
};

const countedText = (countExcused: boolean): string => countExcused ? 'unentschuldigt oder entschuldigt' : 'unentschuldigt';

/** "Löst aus, wenn ein Kind 2 Stunden hintereinander unentschuldigt fehlt." */
export const rulePreviewText = (raw: EarlyWarningRuleForm): string => {
    const form = normaliseRuleForm(raw);
    const what = countedText(form.countExcused);
    if (form.triggerMode === AbsenceEarlyWarningTriggerMode.RATIO) {
        return `Löst aus, wenn ein Kind ${form.threshold} von den letzten ${form.window} protokollierten Stunden ${what} fehlt `
            + 'und auch in der letzten Stunde gefehlt hat.';
    }
    const lessons = form.threshold === 1 ? 'eine Stunde' : `${form.threshold} Stunden hintereinander`;
    return `Löst aus, wenn ein Kind ${lessons} ${what} fehlt.`;
};

/** "Höchstens eine Warnung je Kind alle 6 Wochen · To-do und Mail an die Familie" */
export const ruleActionsText = (raw: EarlyWarningRuleForm): string => {
    const form = normaliseRuleForm(raw);
    const cooldown = form.cooldownWeeks > 0
        ? `höchstens eine Warnung je Kind in ${form.cooldownWeeks} ${form.cooldownWeeks === 1 ? 'Woche' : 'Wochen'}`
        : 'ohne Ruhezeit';
    const actions = [form.toDo ? 'To-do an QM-Zuständige' : '', form.parentMail ? 'Mail an die Familie' : ''].filter(Boolean).join(' und ');
    return `${cooldown.charAt(0).toUpperCase()}${cooldown.slice(1)} · ${actions || 'keine Aktion'}`;
};

export type DryRunTotals = { matches: number; institutions: number; skippedCooldown: number; skippedTerminated: number };

/** "Träfe heute auf 7 Kinder an 5 Standorten zu. 3 davon …" */
export const dryRunText = (totals: DryRunTotals, cooldownWeeks: number): string => {
    const kids = totals.matches === 1 ? 'ein Kind' : `${totals.matches} Kinder`;
    const places = totals.institutions === 1 ? 'einem Standort' : `${totals.institutions} Standorten`;
    const parts = [totals.matches ? `Träfe heute auf ${kids} an ${places} zu.` : 'Träfe heute auf kein Kind zu.'];
    if (totals.skippedCooldown) {
        parts.push(`${totals.skippedCooldown} davon ${totals.skippedCooldown === 1 ? 'wurde' : 'wurden'} in den letzten `
            + `${cooldownWeeks} Wochen schon gemeldet und ${totals.skippedCooldown === 1 ? 'wird' : 'werden'} übersprungen.`);
    }
    if (totals.skippedTerminated) {
        parts.push(`${totals.skippedTerminated} mit Kündigung ${totals.skippedTerminated === 1 ? 'wird' : 'werden'} nicht gemeldet.`);
    }
    return parts.join(' ');
};

export const stateName = (state: StateCodes | string): string => StateCodes.Record[state as StateCodes]?.text || String(state);

export const statesText = (states: Array<StateCodes | string> | undefined): string => (states || []).map(stateName).join(', ') || '–';

const PRESENCE: Record<number, { short: string; text: string; tone: Tone }> = {
    [CoursePresenceState.PRESENT]: {short: 'A', text: 'anwesend', tone: 'good'},
    [CoursePresenceState.ABSENT_WITH_PERMISSION]: {short: 'E', text: 'entschuldigt', tone: 'warn'},
    [CoursePresenceState.ABSENT_WITHOUT_PERMISSION]: {short: 'U', text: 'unentschuldigt', tone: 'bad'},
};

export type PresenceDot = { key: string; short: string; text: string; tone: Tone; date: string };

/** Dots oldest → newest (the API delivers newest first) */
export const presenceDots = (recent: AbsenceEarlyWarningRecentItem[] | undefined): PresenceDot[] => (recent || [])
    .slice()
    .reverse()
    .map((item, index) => {
        const meta = PRESENCE[item.state] || {short: '?', text: 'unbekannt', tone: 'muted' as Tone};
        const date = item.instance ? new Date(item.instance).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit'}) : '';
        return {key: `${index}-${date}`, ...meta, date};
    });

export const presenceDotsLabel = (dots: PresenceDot[]): string => dots.length
    ? `Letzte ${dots.length} Stunden, älteste zuerst: ${dots.map((d) => `${d.date} ${d.text}`).join(', ')}`
    : 'Keine Stunden';

const MAIL_STATUS: Record<AbsenceEarlyWarningMailStatus, { text: string; tone: Tone; icon: string }> = {
    [AbsenceEarlyWarningMailStatus.SENT]: {text: 'Mail verschickt', tone: 'good', icon: 'mdi-email-check-outline'},
    [AbsenceEarlyWarningMailStatus.SKIPPED]: {text: 'Mail übersprungen', tone: 'warn', icon: 'mdi-email-off-outline'},
    [AbsenceEarlyWarningMailStatus.FAILED]: {text: 'Mail fehlgeschlagen', tone: 'bad', icon: 'mdi-email-alert-outline'},
    [AbsenceEarlyWarningMailStatus.DISABLED]: {text: 'ohne Mail', tone: 'muted', icon: 'mdi-email-off-outline'},
};

const MAIL_REASON: Record<AbsenceEarlyWarningMailSkipReason, string> = {
    [AbsenceEarlyWarningMailSkipReason.NO_PARENT]: 'kein aktives Elternteil',
    [AbsenceEarlyWarningMailSkipReason.NO_MAIL]: 'keine Mail-Adresse hinterlegt',
    [AbsenceEarlyWarningMailSkipReason.OPT_OUT]: 'Familie möchte keine Frühwarnungs-Mails',
    [AbsenceEarlyWarningMailSkipReason.NO_TEMPLATE]: 'keine Mail-Vorlage',
    [AbsenceEarlyWarningMailSkipReason.NO_SENDER]: 'keine QM-Zuständige als Absender:in',
    [AbsenceEarlyWarningMailSkipReason.ERROR]: 'Fehler beim Versand',
};

export const mailStatusMeta = (mail: { status?: AbsenceEarlyWarningMailStatus; reason?: AbsenceEarlyWarningMailSkipReason; recipient?: string } | undefined) => {
    const meta = mail?.status ? MAIL_STATUS[mail.status] : undefined;
    const base = meta || {text: 'Mail unbekannt', tone: 'muted' as Tone, icon: 'mdi-email-outline'};
    const details = [mail?.reason ? MAIL_REASON[mail.reason] : '', mail?.recipient ? `an ${mail.recipient}` : ''].filter(Boolean).join(' · ');
    return {...base, details};
};

export const RESPONSIBLE_SOURCE_TEXT: Record<QualityManagementResolutionSource, string> = {
    [QualityManagementResolutionSource.QUALITY_MANAGEMENT_LIST]: 'QM-Zuständige',
    [QualityManagementResolutionSource.BACKUP]: 'Vertretung',
    [QualityManagementResolutionSource.DEFAULT_SUPERVISOR]: 'Standard-Supervisor:in',
    [QualityManagementResolutionSource.NONE]: 'niemand zuständig',
    [QualityManagementResolutionSource.MANUAL]: 'von Hand zugewiesen',
};

/** One QM list (assignee + its Bundesländer) aggregated from the state overview */
export type QualityManagementListRow = {
    _id: string;
    assignee: string;
    assigneeName: string;
    assigneeActive: boolean;
    backup?: string;
    backupName?: string;
    backupActive?: boolean;
    states: StateCodes[];
};

export const listsFromOverview = (overview: QualityManagementStateOverviewItemDto[]): QualityManagementListRow[] => {
    const byId = new Map<string, QualityManagementListRow>();
    for (const item of overview) {
        if (!item.list) {
            continue;
        }
        const id = String(item.list._id);
        const row = byId.get(id) || {
            _id: id,
            assignee: String(item.list.assignee),
            assigneeName: item.list.assigneeName || 'Unbekannte Person',
            assigneeActive: item.list.assigneeActive,
            backup: item.list.backup ? String(item.list.backup) : undefined,
            backupName: item.list.backupName,
            backupActive: item.list.backupActive,
            states: [],
        };
        row.states.push(item.state);
        byId.set(id, row);
    }
    return Array.from(byId.values());
};

export const initials = (name: string | undefined): string => (name || '')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || '?';

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** "Gitarre KS012 · Di 14:00 · Adolf-Kußmaul-Schule, Graben-Neudorf" (dayOfWeek 0 = Monday, startTime in minutes) */
export const courseLine = (item: {
    subjectName?: string; courseCustomId?: string; dayOfWeek?: number; startTime?: number;
    institution?: { name?: string; city?: string };
}): string => {
    const time = typeof item.startTime === 'number'
        ? `${String(Math.floor(item.startTime / 60)).padStart(2, '0')}:${String(item.startTime % 60).padStart(2, '0')}` : '';
    const day = typeof item.dayOfWeek === 'number' ? WEEKDAY_SHORT[item.dayOfWeek] || '' : '';
    const place = [item.institution?.name, item.institution?.city].filter(Boolean).join(', ');
    return [[item.subjectName, item.courseCustomId].filter(Boolean).join(' '), [day, time].filter(Boolean).join(' '), place]
        .filter(Boolean).join(' · ');
};

export const formatDateTime = (value: Date | string | undefined): string => value
    ? new Date(value).toLocaleString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})
    : '–';

/** Percent of warnings, rounded; '–' without warnings */
export const percentOf = (value: number, total: number): string => total > 0 ? `${Math.round(value / total * 100)} %` : '–';

/** "3 Warnungen, 2 Mails verschickt, 1 übersprungen (Ruhezeit)" */
export const runSummaryText = (summary: Partial<AbsenceEarlyWarningRunSummary> | undefined): string => {
    if (!summary) {
        return 'Regel ausgeführt';
    }
    const warnings = summary.warnings ?? 0;
    const parts = [`${warnings} ${warnings === 1 ? 'Warnung' : 'Warnungen'}`];
    if (summary.mailsSent) {
        parts.push(`${summary.mailsSent} ${summary.mailsSent === 1 ? 'Mail' : 'Mails'} verschickt`);
    }
    if (summary.skippedCooldown) {
        parts.push(`${summary.skippedCooldown} wegen Ruhezeit übersprungen`);
    }
    if (summary.errors) {
        parts.push(`${summary.errors} Fehler`);
    }
    return parts.join(' · ');
};
